import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCenter, setCountry } from '../../../redux/slices/centerSlice';
import { setCampCenters, setLoading, setError } from '../../../redux/slices/centersListSlice';
import campService from '../services/campService';
import '../../SharedTheme.css';

const CampCenter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);

  const { campCenters, loading, error } = useSelector((state) => state.centersList);
  const selectedCenter = useSelector((state) => state.center);

  const centers = campCenters || [];
  const localLoading = loading;
  const localError = error;

  const [countryId, setCountryId] = useState('USA');

  useEffect(() => {
    if (fetchingRef.current) return;
    // If we already have cached centers, show them immediately and refresh silently
    if (centers.length > 0) {
      fetchCenters(countryId, true); // silent = true
    } else {
      fetchCenters(countryId, false);
    }
  }, [countryId]);

  const fetchCenters = async (country, silent = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      if (!silent) dispatch(setLoading(true));
      let data;
      try {
        data = await campService.getCenters(country);
      } catch {
        await new Promise((r) => setTimeout(r, 1500));
        data = await campService.getCenters(country);
      }
      dispatch(setCampCenters(data || []));
      dispatch(setError(null));
    } catch (err) {
      if (!silent) dispatch(setError('Failed to load centers. Please try again later.'));
      console.error(err);
    } finally {
      if (!silent) dispatch(setLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleSelectCenter = (center) => {
    dispatch(setCenter({
      center_Id: center.center_Id,
      center_Name: center.center_Name,
      center_Desc: center.center_desc || center.center_Desc,
      center_Logo_URL: center.center_Logo_URL,
      country_Desc: countryId,
    }));
    dispatch(setCountry(countryId));
    navigate(`/camp/selection?centerId=${center.center_Id}&country=${countryId}`);
  };

  const handleCountryChange = (e) => {
    setCountryId(e.target.value);
  };

  return (
    <div className="camp-center-page yk-page-bg min-h-screen py-16">
      <div className="container-custom">

        {/* Header — same as ClassCenter */}
        <div className="mb-12 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
              Select Your Camp Center
            </h1>
            <p className="text-sm text-gray-500">
              Discover amazing camps at our premium centers
            </p>
          </div>
        </div>

        {/* Loading */}
        {localLoading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading centers...</p>
          </div>
        )}

        {/* Error */}
        {localError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{localError}</p>
          </div>
        )}

        {/* Centers Grid — same as ClassCenter */}
        {!localLoading && centers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className={`item-card cursor-pointer flex flex-col ${
                  selectedCenter?.center_Id === center.center_Id ? 'selected' : ''
                }`}
                onClick={() => handleSelectCenter(center)}
              >
                {/* Selected tick */}
                {selectedCenter?.center_Id === center.center_Id && (
                  <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                    <svg className="w-5 h-5 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}

                {/* Image */}
                {center.center_Logo_URL && (
                  <div className="h-56 bg-gradient-to-br from-blue-400 to-purple-400 overflow-hidden relative rounded-t-[18px]">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Camp+Center'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                )}

                {/* Content */}
                <div className="p-6 relative z-10 flex flex-col flex-1">
                  <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2 leading-snug">
                    {center.center_Name}
                  </h3>

                  <p className="text-gray-600 mb-5 text-sm leading-relaxed flex-1">
                    {center.center_desc || center.center_Desc}
                  </p>

                  <button className="yk-btn w-full font-semibold py-2 px-4 rounded-full text-sm mt-auto">
                    Explore Camps
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!localLoading && centers.length === 0 && !localError && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No centers available for the selected country.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampCenter;
